/**
 * Sprint V100.1 (17.09.2026) — Einzelne Frage im 20-Fragen-Test-Framework.
 *
 * UX-Konsistenz: identisches Rendering wie DashboardTest (5 Smiley-Zeilen,
 * Progress-Bar, „Frage X von N"-Label, Zurück-Button). NICHT mehr Zahlen 1-5
 * als Buttons — der User will die gleiche Darstellungssprache wie im
 * 20-Fragen-Test, nur mit weniger Fragen.
 */
import React from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { LIKERT_OPTIONS } from "../../data/quickCheckV100Data";

export default function QuickCheckLikertQuestion({
  qid,
  qIndex,
  qTotal,
  text,
  value,
  pendingValue,
  submitting,
  onChange,
  onBack,
}) {
  const progress = Math.round(((qIndex + (value ? 1 : 0)) / qTotal) * 100);
  const isLast = qIndex === qTotal - 1;

  return (
    <div
      className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 sm:p-10"
      data-testid={`v100-q${qid}`}
    >
      {/* Progress-Header */}
      <div className="flex items-center justify-between text-xs font-bold uppercase tracking-widest">
        <span
          className="text-slate-500 dark:text-slate-400"
          data-testid="v100-q-progress-label"
        >
          Frage {qIndex + 1} von {qTotal}
        </span>
        <span className="text-[#4563BB]">{progress}%</span>
      </div>
      <div
        className="mt-2 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden"
        data-testid="v100-progress-bar"
      >
        <div
          className="h-full bg-[#4563BB] transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Frage-Text */}
      <h2
        className="mt-7 text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-snug"
        data-testid={`v100-q${qid}-text`}
      >
        {text}
      </h2>
      <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
        Wie gut trifft das auf dich zu?
      </p>

      {/* 5 Smiley-Zeilen */}
      <div className="mt-7 space-y-2" role="radiogroup" aria-label={`Frage ${qIndex + 1}`}>
        {LIKERT_OPTIONS.map((opt) => {
          const selected = value === opt.value;
          const isPending = pendingValue === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              disabled={!!pendingValue || submitting}
              role="radio"
              aria-checked={selected}
              data-testid={`v100-q${qid}-value-${opt.value}`}
              className={`w-full flex items-center gap-4 rounded-xl border px-4 py-3.5 text-left transition-all ${
                selected || isPending
                  ? "border-[#4563BB] bg-[#4563BB]/[0.06] dark:bg-[#4563BB]/[0.15] shadow-sm scale-[0.99]"
                  : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500"
              } disabled:cursor-wait`}
            >
              <span className="text-2xl flex-shrink-0" aria-hidden="true">
                {opt.smiley}
              </span>
              <span
                className={`text-sm sm:text-base font-medium ${
                  selected || isPending
                    ? "text-slate-900 dark:text-white"
                    : "text-slate-700 dark:text-slate-200"
                }`}
              >
                {opt.label}
              </span>
              <span
                className={`ml-auto w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                  selected || isPending
                    ? "border-[#4563BB] bg-[#4563BB]"
                    : "border-slate-300 dark:border-slate-600"
                }`}
              />
            </button>
          );
        })}
      </div>

      {/* Footer: Zurück + optionaler Submit-State */}
      <div className="mt-8 flex items-center justify-between gap-3 min-h-[2.5rem]">
        <button
          type="button"
          onClick={onBack}
          disabled={qIndex === 0 || submitting || !!pendingValue}
          data-testid="v100-q-back-btn"
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>
        {(submitting || (isLast && pendingValue)) && (
          <span
            className="inline-flex items-center gap-2 text-sm text-[#4563BB] font-semibold"
            data-testid="v100-q-submitting"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Ergebnis wird ausgewertet &hellip;
          </span>
        )}
      </div>
    </div>
  );
}
